import { Canvas } from '@react-three/fiber'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { getVideoCodec, setTokenStorage } from '../utils'
import axiosClient from '../axiosInstance'
import { config } from '../../config'
import { SceneInfoType } from '../types/sceneInfoType'
import { ImageViewer } from './ImageViewer'
import { VideoViewer } from './VideoViewer'
import { Caption3d } from './Caption3d'
import { CameraController } from './CameraController'
import { AuthResponseType } from '../types/authResponseType'
import { SignedCookieResponseType } from '../types/signedCookieResponseType'
import { ListSpaceResponseType, OrderedSpace } from '../types/listSpaceResponseType'
import { SceneInfoResponseType } from '../types/sceneInfoResponseType'
import { useCreateVideo } from '../hooks/useCreateVideo'

import { useTone } from '../hooks/useTone'

import { MuteIcon, UnmuteIcon, VideoIcon, CopyIcon } from '@primer/octicons-react'
import { shuffle, sample } from 'es-toolkit'

export const Player = () => {
  const isFetched = useRef(false)
  const refCanvas = useRef<HTMLCanvasElement>(null)

  const [isLoading, setIsLoading] = useState(true)
  const [listSpace, setListSpace] = useState<OrderedSpace[] | null>(null)
  const [isMute, setIsMute] = useState(true)
  const [spaceId, setSpaceId] = useState<number>(-2)
  const [progress, setProgress] = useState<number>(0)
  const [progressPosition, setProgressPosition] = useState<number[] | null>(null)
  const [currentPlayer, setCurrentPlayer] = useState<string>('A')
  const [sceneFov, setSceneFov] = useState<number>(75)
  const [autoPilotState, setAutoPilotState] = useState<boolean>(true)
  // const [autoPilotState, setAutoPilotState] = useState<boolean>(true)
  const [cameraPositionRequest, setCameraPositionRequest] = useState<number>(0)
  const scenesRef = useRef<Array<any>>([])
  // for A
  const video = useCreateVideo()
  const [sceneId, setSceneId] = useState<number | null>(null)
  const [sceneInfo, setSceneInfo] = useState<SceneInfoType | null>(null)
  const [startPos, setStartPos] = useState<number>(0)
  const [playLength, setPlayLength] = useState<number>(0.975)
  // for B
  const videoAlt = useCreateVideo()
  const [sceneIdAlt, setSceneIdAlt] = useState<number | null>(null)
  const [sceneInfoAlt, setSceneInfoAlt] = useState<SceneInfoType | null>(null)
  const [startPosAlt, setStartPosAlt] = useState<number>(0)
  const [playLengthAlt, setPlayLengthAlt] = useState<number>(0.975)
  // Audio efx
  const toneInit = useRef<boolean>(false)
  const tone = useTone()

  const fetch = useCallback(async () => {
    try {
      const projectId = config.PUBLISH_PROJECT_ID

      // auth API
      const authResponse = await axios.post<AuthResponseType>(
        `${config.PLAYER_AUTH_API}`,
        { projectId: projectId },
        { headers: { 'X-API-KEY': `${config.X_API_KEY}` }, withCredentials: true }
      )
      if (authResponse?.data.statusCode === 200) {
        const authData = authResponse.data.data
        setTokenStorage(authData)
      } else {
        throw new Error('Failed to authenticate')
      }

      // signedCookies API
      const signedCookieResponse = await axiosClient.get<SignedCookieResponseType>(
        `${config.PROJECT_SIGNED_COOKIES_API}`
      )
      if (signedCookieResponse?.data.statusCode === 200) {
        const data = signedCookieResponse.data.data
        const cookiesData: { [key: string]: string } = {
          'CloudFront-Policy': data['cloudFrontPolicy'],
          'CloudFront-Signature': data['cloudFrontSignature'],
          'CloudFront-Key-Pair-Id': data['cloudFrontKeyPairId'],
        }

        const setCookie = (cname: string, cvalue: string) => {
          document.cookie = `${cname}=${cvalue};path=/;domain=${config.COOKIE_DOMAIN};SameSite=None;Secure`
        }

        for (const key in cookiesData) {
          setCookie(key, cookiesData[key])
        }
      } else {
        throw new Error('Failed to get signed cookies')
      }

      // listSpace API
      const listSpaceResponse = await axiosClient.get<ListSpaceResponseType>(`${config.LIST_SPACE_API}?langCode=en`)
      const listSpaceData = listSpaceResponse.data
      if (listSpaceData.statusCode === 200) {
        setListSpace(listSpaceData.data.orderStructure.layers[0].spaces)
      } else {
        throw new Error('Failed to list space')
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  /* for initialize */
  useEffect(() => {
    if (!listSpace) {
      return
    }

    randomSwitching('A')

  }, [listSpace])

  useEffect(() => {
    if (isFetched.current) {
      return
    }

    isFetched.current = true
    setIsLoading(true)

    fetch()
  }, [])
  /* -- */

  const onLoaded = useCallback(() => {
    setIsLoading(false)
    if ('H265_HLS' == getVideoCodec()) {
      // console.log('webkit browser fallback')
      video.muted(spaceId == 0 || spaceId == 1 ? false : true)
      video.volume(spaceId == 0 ? 0.65 : (spaceId == 1 ? 1 : 0))
      videoAlt.muted(spaceId == 0 ? false : true)
      videoAlt.volume(spaceId == 0 ? 0.65 : 0)
    }
  }, [video, videoAlt, spaceId])

  // sceneInfo API
  const fetchSceneInfo = async (id: number, cb: (arg: number) => void, cbInfo: (arg: SceneInfoType) => void) => {
    cb(id)

    try {
      const codec = getVideoCodec()

      const sceneInfoResponse = await axiosClient.get<SceneInfoResponseType>(
        `${config.SCENE_INFO_API}${id}?langCode=en&contextCodec=${codec}`
      )
      if (sceneInfoResponse.data.statusCode === 200) {
        const data = sceneInfoResponse.data.data
        console.info(data.title)
        cbInfo({
          id: data.sceneId,
          contextType: data.contextType,
          contextUrl: data.contextAttributes[0].url,
          timestamp: Date.now(),
        })
      } else {
        throw new Error('Failed to get scene info')
      }
    } catch (error) {
      console.error('fetch error', error)
      return Promise.reject()
    }
  }

  // w/ fading
  const randomSwitching = useCallback(
    async (playerKey: string) => {
      if (!listSpace) {
        return
      }

      if (scenesRef.current.length == 0) {
        scenesRef.current = shuffle(listSpace[0]['scenes'])
      }

      const r = sample([4, 4, 4, 4, 6, 6, 6, 8, 8])
      const r2 = sample([0, Math.random() * 0.5, 0.5 + Math.random() * 0.4])

      if (playerKey == 'A') {
        const scene = (scenesRef.current[0]['id'] == sceneIdAlt) ? scenesRef.current.pop() : scenesRef.current.shift()
        setPlayLength(r)
        setStartPos(r2)
        await fetchSceneInfo(scene.id, setSceneId, setSceneInfo).catch((_) => {
          console.error('retry..')
          setTimeout(() => randomSwitching(playerKey), 100)
        })
        setCurrentPlayer('A')
      } else {
        const scene = (scenesRef.current[0]['id'] == sceneId) ? scenesRef.current.pop() : scenesRef.current.shift()
        setPlayLengthAlt(r)
        setStartPosAlt(r2)
        await fetchSceneInfo(scene.id, setSceneIdAlt, setSceneInfoAlt).catch((_) => {
          console.error('retry..')
          setTimeout(() => randomSwitching(playerKey), 100)
        })
        setCurrentPlayer('B')
      }

      console.info('On player', playerKey, ' | marker: ', r2, ' / ', r)

      if (toneInit.current) { changeAudioParameters() }
    },
    [listSpace, spaceId, sceneId, sceneIdAlt]
  )

  const setAudioContext = () => {
    tone.attachStream(video, 'A')
    tone.attachStream(videoAlt, 'B')
  }

  const changeAudioParameters = (reset: boolean = false) => {
    tone.randomFx()
  }

  const playNextSpace = useCallback(async () => {
    if (!listSpace || !listSpace[1]?.scenes?.[0]) {
      return
    }
    setSpaceId((n) => {
      return Math.min(n + 1, 1)
    })
  }, [listSpace])

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  useEffect(() => {
    if (spaceId == -1) {
      tone.start()
      setIsMute(false)

      if (!toneInit.current) {
        setAudioContext()
        toneInit.current = true
      }
    }

    if (spaceId == 0) {
      if (toneInit.current) {
        tone.mix2()
      }
    }

    if (spaceId < 1) return

    const af = async () => {
      const longScene = listSpace[1].scenes[0]
      let c = 0
      const ci = setInterval(() => {
        setProgress((prev) => prev + 0.01)
        c++
        if (c > 100) { clearInterval(ci) }
      }, 1000 / 30);

      await sleep(500)
      await fetchSceneInfo(longScene.id, setSceneId, setSceneInfo)

      if (toneInit.current) {
        tone.passThrough()
      }
    }
    af()
  }, [listSpace, spaceId])

  const onEnded = useCallback(() => {
    // console.log('ended')
    setSpaceId(2)
  }, [listSpace])

  const checkLongPress = useCallback((s:boolean, t:number, o:any) => {
    setProgress(t)

    if (o) {
      const { type = null, position = [] } = o
      if (type == 'touchstart' && position && position.length > 0) {
        const {x, y} = position[0]
        setProgressPosition([x,y])
      }
    }
  }, [])

  useEffect(() => {
    if (autoPilotState && spaceId < 1) {
      randomSwitching('A')
    }
  }, [autoPilotState, spaceId])

  useEffect(() => {
    if (tone) {
      tone.mute(isMute)
    }
  }, [isMute, tone])

  const toggleMute = () => {
    setIsMute((prev) => { return !prev })
  }

  return (
    <>
      {spaceId > 1 && (
        <div
          style={{
            width: '100vw',
            height: '100svh',
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            background: 'rgba(26, 26, 26, 1)',
          }}
        >
          <a style={{ color: 'white' }} href="./">
            <div className="title active">
              <img src="output-title.apng" />
            </div>
          </a>
        </div>
      )}

      <Canvas
        ref={refCanvas}
        onClick={
          spaceId < 1 ? () => { playNextSpace() } : () => {}
        }
        flat
        style={{ width: '100%', height: '100%', userSelect: 'none' }}
        camera={{ far: 10000, zoom: 0.7, fov: 75 }}
      >
        <Suspense fallback={null}>
          {sceneInfo?.contextType === 'image' && (
            <ImageViewer key={sceneInfo?.id} src={sceneInfo?.contextUrl} onLoaded={onLoaded} />
          )}

          {sceneInfo?.contextType === 'video' && spaceId < 2 && (
            <VideoViewer
              key={`${sceneInfo?.id}-${sceneInfo?.timestamp}_A`}
              video={video}
              src={sceneInfo?.contextUrl}
              isLoop={false}
              isMute={isMute}
              onLoaded={onLoaded}
              // onMarkerPassed={() => { randomSwitching('B') }}
              onEnded={
                spaceId < 1 ? () => { randomSwitching('B') } : onEnded
              }
              // onClick={spaceId < 0 ? playNextSpace : () => {}}
              startMarker={(spaceId < 1 && autoPilotState) ? startPos : 0}
              marker={(spaceId < 1 && autoPilotState) ? playLength : null}
              onCheckPointPassed={(spaceId < 1) ? () => {} : () => { setCameraPositionRequest((prev) => prev + 1)}}
              // startMarker={0}
              // marker={3}
              // position={[-4, 0, -10]}
              // radius={10}
            />
          )}

          {sceneInfoAlt?.contextType === 'video' && spaceId < 1 && (
            <VideoViewer
              key={`${sceneInfoAlt?.id}-${sceneInfoAlt?.timestamp}_B`}
              video={videoAlt}
              src={sceneInfoAlt?.contextUrl}
              isLoop={false}
              isMute={isMute}
              onLoaded={onLoaded}
              // onMarkerPassed={() => { randomSwitching('A') }}
              onEnded={() => { randomSwitching('A') }}
              startMarker={(spaceId < 1 && autoPilotState) ? startPosAlt : 0}
              marker={(spaceId < 1 && autoPilotState) ? playLengthAlt : null}
              // startMarker={0}
              // marker={3}
              // position={[4, 0, -10]}
              // radius={10}
            />
          )}

          {spaceId < 1 && <Caption3d refCanvas={refCanvas} vCenter={1} dist={-2} mode={spaceId} />}

          <CameraController
            refCanvas={refCanvas}
            id={sceneInfo?.id}
            fov={sceneFov}
            cameraReset={cameraPositionRequest}
            controlsMode={spaceId}
            onAutoPilot={(b:boolean) => { spaceId < 1 ? setAutoPilotState(true) : setAutoPilotState(false) } }
          />

        </Suspense>
      </Canvas>

      {spaceId > -2 && (
        <div className="button-wrapper">
          {/*(
          <button onClick={() => { window.open('https://yokohama.art.museum/exhibition/202502_kairos/', '_blank') }}>
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="32" height="32" fill="currentColor" display="inline-block" overflow="visible" style={{verticalAlign: 'text-bottom'}}>
              <text x="7.5" y="18.5" style={{ fontSize: '20px' }}>?</text>
            </svg>
          </button>
          )*/}
          <button onClick={() => { window.open('https://yokohama.art.museum/exhibition/202502_kairos/', '_blank') }}>
            <CopyIcon size="medium" />
          </button>
          {spaceId < 1 && ( // NOTE: it will change to spaceId < 1
            <button onClick={toggleMute}>
              {isMute && <MuteIcon size="medium" />}
              {!isMute && <UnmuteIcon size="medium" />}
            </button>
          )}
          {spaceId == 0 && (
            <button onClick={playNextSpace}>
              <VideoIcon size="medium" />
            </button>
          )}
        </div>
      )}

      {spaceId <= 1 && (
        <div className={ progress > 0.1 && progress < 0.99 ? 'longPressWait active' : 'longPressWait' }>
          <img style={{ width: `${Math.max(90 * progress, 10)}vw`, opacity: `${Math.pow(1 - progress * 0.8, 1.4)}` }} src="output.apng" />
        </div>
      )}

      {spaceId < 0 && listSpace && (
        <div
          className={ spaceId < -1 ? 'title active' : 'title' }>
          <img src="output-title.apng" />
        </div>
      )}
    </>
  )
}
